import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, SafeAreaView, View } from 'react-native';
import { Text } from '@/components/Text';
import { AxiosError } from 'axios';
import { EvilIcons, Ionicons, Entypo } from '@expo/vector-icons';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import store from '@/stores/store';
import {
  serverSyncPatientActiveSets,
  serverSyncPatientSettings,
} from '../../api/services/patient';
import {
  downloadPatient,
  localLogoutUser,
  permission,
} from '../../api/services/user';
import { TherapistNoticeModal } from '@/components/Home/TherapistNoticeModal';
import CustomDropdown from '@/components/SharedComponents/CustomDropdown/CustomDropdown';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import ProgressModal from '@/components/SharedComponents/ProgressModal/ProgressModal';
import colors from '@/config/colors';
import { useGlobalStateInHome } from '@/hooks/useGlobalStateInHome';
import { serverLogoutUser } from '../../stores/auth/services';
import encryptedStorage from '../../stores/encryptedStorage';
import DownloadManager from '../../utils/DownloadManager';
import starUtil from '@/utils/starUtil';
import {
  compareTimeStamps,
  generateSets,
  loadAndClean,
  loadSets,
} from './helpers';
import { startCheckConsent } from '../../utils/consents';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Icon } from '@/components/Icon';
import { StarsCharts } from '@/components/Home/StarsCharts';
import { DemoModal } from '@/components/Home/DemoModal';
import { ExerciseCard } from '../Aufgaben/ExerciseCard';
import { logError } from '@/utils/logHandling';
import { HomeMain } from './main';
import { useWindowResize } from '@/hooks/useWindowResize';
import { useStoreActions, useStoreState } from '@/stores/store';
import { SentenceSet, TextSet, WordSet } from '@/stores/store.types';
import { DEVICE_TOKEN_KEY } from '@/utils/secureDeviceToken';
import { isTextSet, isWordSet } from '@/utils/isExerciseSet';

const { upload, applyUserId } = starUtil;

// Initialize this function only once during mounting
const determineSetType = (setType: 'textset' | 'wordset' | 'sentenceset') => {
  if (setType === 'textset') {
    return 'Texte';
  } else if (setType === 'wordset') {
    return 'Wörter';
  } else {
    return 'Sätze';
  }
};

export function Home() {
  const textSize = useResponsiveFontSize(4);
  const iconSize = useResponsiveFontSize(9);
  const isFocused = useIsFocused();
  const { isPatientDemo, changeActivePatient, signedInUser } =
    useGlobalStateInHome();
  const navigation = useNavigation();
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [noTherapistNotice, setNoTherapistNotice] = useState(false);
  const [showAssetDownloadModal, setShowAssetDownloadModal] = useState(false);
  const [assetDownloadProgress, setAssetDownloadProgress] = useState(0);
  const [localSets, setLocalSets] = useState<{
    [setId: string]: WordSet | SentenceSet | TextSet;
  }>({});
  const activeSet = useStoreState((store) => store.patientActiveSet);
  const setActiveSet = useStoreActions(
    (actions) => actions.changePatientActiveSet,
  );
  const [logoutFromDemo, setLogoutFromDemo] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDemoPopUp, setShowDemoPopUp] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [demoTimerCounter, setDemoCounterTimer] =
    useState<NodeJS.Timeout | null>(null);

  useWindowResize();

  const activeSets = useMemo(() => {
    return Object.keys(localSets);
  }, [localSets]);

  const hideExpandedMenu = useCallback(() => {
    setExpanded(false);
  }, []);
  const toggleExpandedMenu = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const isLandscape = useResponsiveHeight(100) < useResponsiveWidth(100);

  const useResponsiveSize = (size: number) => {
    if (isLandscape) {
      return useResponsiveWidth(size);
    } else {
      return useResponsiveHeight(size);
    }
  };

  const responsizeIconSizeBig = useResponsiveSize(10);

  const downloadExercises = useCallback(
    async (wordsets: { [setId: string]: WordSet | SentenceSet | TextSet }) => {
      try {
        const activePatient = store.getState().activePatient;
        const setsThatNeedSyncing = compareTimeStamps(activePatient, wordsets);

        const promises = [];
        const wordsetIds = [];
        const sentencesetIds = [];
        const textsetIds = [];
        for (let setId in wordsets) {
          const localWordset = wordsets[setId];
          if (localWordset.exerciseClass === 'sentence') {
            if (setsThatNeedSyncing.includes(setId)) sentencesetIds.push(setId);
          } else if (localWordset.exerciseClass === 'text') {
            if (setsThatNeedSyncing.includes(setId)) textsetIds.push(setId);
          } else {
            if (setsThatNeedSyncing.includes(setId)) wordsetIds.push(setId);
          }
        }

        const payload = {
          setsToFetch: {
            wordsets: wordsetIds,
            sentencesets: sentencesetIds,
            textsets: textsetIds,
          },
        };

        const { data } = await serverSyncPatientActiveSets(payload);

        for (let setId in wordsets) {
          const localWordset = wordsets[setId];
          let exerciseClass = 'word';
          if (localWordset.exerciseClass === 'sentence') {
            exerciseClass = 'sentence';
          } else if (localWordset.exerciseClass === 'text') {
            exerciseClass = 'text';
          }
          promises.push(
            await downloadPatient(
              setId,
              localWordset,
              data[exerciseClass + 'sets'],
            ),
          );
        }
      } catch (error) {
        logError('Could not download exercise', error);
      }
    },
    [],
  );

  const downloadAssetsOnMobilePlatforms = async () => {
    try {
      if (Platform.OS === 'web') return;
      let allAssetsLinks: string[] = [];
      let allSetsState = {};
      let keys = await encryptedStorage().listItems();
      let allSetsStateArray = keys.filter(
        (k) =>
          k !== '[encryptedStorage][0]' &&
          k !== 'stayLoggedIn' &&
          k !== 'minimumAppVersion' &&
          k !== 'seenUpdates' &&
          k !== 'unprotectedDevice' &&
          k !== 'unsupportedIosVersionContinue' &&
          k !== 'unsupportedAndroidVersionContinue' &&
          !k.includes('sessionBuffer') &&
          !k.includes('starContainer') &&
          !k.includes('__audio__cache') &&
          k !== DEVICE_TOKEN_KEY,
      );

      await Promise.all(
        allSetsStateArray.map(async (key) => {
          const value = await encryptedStorage().getItem(key);
          return (allSetsState[key] = value);
        }),
      );
      for (let key in allSetsState) {
        const localWordset = allSetsState[key];
        const isSync = localWordset.sync;
        if (!isSync) continue;
        const allAssetsFlattened = localWordset.assets.flat();
        allAssetsLinks = [...allAssetsLinks, ...allAssetsFlattened];
      }

      DownloadManager.downloadAssets(
        allAssetsLinks,
        setAssetDownloadProgress,
        setShowAssetDownloadModal,
      );
    } catch (error) {
      logError('downloadAssetsOnMobilePlatforms ', error);
    }
  };

  const syncPatientSettings = async () => {
    try {
      if (noTherapistNotice) setNoTherapistNotice(false);

      if (signedInUser?.user?._id) {
        applyUserId(signedInUser.user._id);
      }

      try {
        upload(false, false);
      } catch (error) {
        logError('Upload failed', error);
      }

      const hasInternet = await hasInternetAsync();
      const { data } = hasInternet
        ? await serverSyncPatientSettings()
        : { data: store.getState().activePatient };

      const patient = [{ ...signedInUser, ...data }];
      changeActivePatient({ data: patient[0] });
      const { patientIdsToKeep, promises, setsToKeep } = generateSets(patient);
      await Promise.all(promises);
      const sets = hasInternet
        ? ((await loadAndClean(setsToKeep, patientIdsToKeep)) as {
            [setId: string]: WordSet | SentenceSet | TextSet;
          })
        : ((await loadSets()) as {
            [setId: string]: WordSet | SentenceSet | TextSet;
          });

      await downloadExercises(sets);
      await downloadAssetsOnMobilePlatforms();
      setLocalSets(sets);
    } catch (e: unknown) {
      setLocalSets({});
      if (
        e instanceof AxiosError &&
        e.response?.data?.message?.includes('Therapeuten')
      ) {
        setNoTherapistNotice(e.response?.data?.message);
      }
    }
    await startCheckConsent();
  };

  const handleStarIconPress = useCallback(() => {
    try {
      setShowProgressModal(true);
    } catch (error) {
      logError('Could not show modal', error);
    }
  }, []);

  const handleFurtherIconPress = useCallback(() => {
    const selectedPatient = signedInUser;
    const activeWordset = localSets[activeSet];

    const exerciseClass = activeWordset.exerciseClass + 'set';

    const param = {
      activeSets,
      exerciseClass,
      exerciseSettings: activeWordset,
      isSelf: true,
      setId: activeSet,
      selectedPatient,
      patientId: selectedPatient.user?._id,
    };
    navigation.navigate('Aufgaben', param);
  }, [localSets, activeSet, activeSets]);

  const rightButtons = useMemo(() => {
    if (noTherapistNotice) {
      return [];
    }
    const buttonsRightSide = [
      {
        id: 1,
        iconName: 'star' as const,
        color: colors.yellow,
        text: null,
        onPress: handleStarIconPress,
      },
      {
        id: 2,
        testID: 'weiter-patient-home',
        iconName: 'arrow-bold-right' as const,
        color: colors.primary,
        text: 'weiter',
        textColor: colors.primary,
        onPress: handleFurtherIconPress,
      },
    ].map((btn) => {
      const { id, iconName, color, text, textColor, testID, onPress } = btn;
      const onlyIcon = text === null;

      return (
        <PressableNeolexon key={id} onPress={onPress} testID={testID}>
          {onlyIcon ? (
            <Entypo name={iconName} color={color} size={iconSize} />
          ) : (
            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Entypo name={iconName} color={color} size={iconSize} />
              <Text
                style={{
                  color: textColor,
                  fontSize: textSize,
                  fontWeight: 700,
                }}
              >
                {text}
              </Text>
            </View>
          )}
        </PressableNeolexon>
      );
    });
    return buttonsRightSide;
  }, [responsizeIconSizeBig, noTherapistNotice, handleFurtherIconPress]);

  useEffect(() => {
    if (activeSets.length === 0) return;

    const setsKeys = Object.keys(localSets);
    if (!setsKeys.includes(activeSet) || activeSet === '') {
      setActiveSet(setsKeys[0]);
    }
  }, [activeSets, localSets, activeSet]);

  const exerciseSets = () => {
    if (activeSets.length === 0) return [];

    return activeSets?.map((setKey, _key) => {
      const { exerciseClass, activeWordset } = localSets[setKey];

      if (!activeWordset) return [];

      const keyName = (exerciseClass + 'set') as
        | 'textset'
        | 'wordset'
        | 'sentenceset';
      const data = activeWordset[keyName];

      const isActive = activeSet === data._id;
      const label = data.appDisplayName
        ? data.appDisplayName
        : determineSetType(keyName);

      const onPress = () => {
        setActiveSet(data._id);
      };

      const imageSource = isTextSet(localSets[setKey])
        ? require('../../assets/text_icon.png')
        : isWordSet(localSets[setKey])
          ? {
              uri: localSets[setKey].activeWordset.wordset.wordsetItems[0].word
                .pictures[0],
            }
          : {
              uri: localSets[setKey].activeWordset.sentenceset
                .sentencesetItems[0].sentence.pictures[0],
            };

      return (
        <ExerciseCard
          key={label}
          label={label}
          onPress={onPress}
          isActive={isActive}
          imageSrc={imageSource}
        />
      );
    });
  };

  useEffect(() => {
    const objKeys = Object.keys(signedInUser);
    if (!isFocused) return;
    if (objKeys.length === 0) return;
    (async () => {
      try {
        await Promise.all([permission(), syncPatientSettings()]);
      } catch (error) {
        logError('Could not sync patients', error);
      }
    })();
  }, [isFocused, signedInUser]);

  useFocusEffect(
    useCallback(() => {
      setExpanded(false);
    }, []),
  );

  useEffect(() => {
    if (!isPatientDemo) return;
    setDemoCounterTimer(
      setTimeout(() => {
        setShowDemoPopUp(true);
      }, 300000),
    );

    return () => {
      if (!isPatientDemo) return;
      if (demoTimerCounter) {
        clearTimeout(demoTimerCounter);
      }
    };
  }, [isPatientDemo]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <AppHeaderAction
          primary
          onPress={() => syncPatientSettings()}
          icon={<EvilIcons name="refresh" scale={1.8} />}
        />
      ),
      title: 'Patient:innen',
      headerRight: () =>
        isPatientDemo ? (
          <AppHeaderAction
            primary
            onPress={() => {
              setLogoutFromDemo(true);
              setShowDemoPopUp(true);
            }}
            icon={<Ionicons name="power-outline" />}
            text="Demo beenden"
          />
        ) : (
          <AppHeaderAction
            primary
            testID="expanded-menu"
            onPress={toggleExpandedMenu}
            icon={<Icon name="settings" />}
          />
        ),
    });
  }, [navigation, signedInUser]);

  useEffect(() => {
    if (isPatientDemo) {
      setShowWelcomeModal((prev) => !prev);
    }
  }, [isPatientDemo]);

  if (noTherapistNotice)
    return (
      <TherapistNoticeModal
        noTherapistNotice={noTherapistNotice}
        localLogoutUser={localLogoutUser}
        serverLogoutUser={serverLogoutUser}
        setNoTherapistNotice={setNoTherapistNotice}
        syncPatientSettings={syncPatientSettings}
      />
    );

  return (
    <SafeAreaView className="flex-1 bg-black">
      <StarsCharts
        isVisible={showProgressModal}
        onConfirm={setShowProgressModal}
      />
      <ProgressModal
        isVisible={showAssetDownloadModal}
        progress={assetDownloadProgress}
        cancelAction={() => {
          setShowAssetDownloadModal(false);
          // DownloadManager.stopDownload();
        }}
      />
      <View className="flex-1 bg-blue-neolexonlight">
        <CustomDropdown
          expanded={expanded}
          hideExpandedMenu={hideExpandedMenu}
        />
        <HomeMain exerciseSets={exerciseSets()} rightButtons={rightButtons} />
      </View>
      <DemoModal
        logoutFromDemo={logoutFromDemo}
        setLogoutFromDemo={setLogoutFromDemo}
        showDemoPopUp={showDemoPopUp}
        setShowDemoPopUp={setShowDemoPopUp}
        demoTimerCounter={demoTimerCounter}
        setDemoCounterTimer={setDemoCounterTimer}
      />
      <ConfirmModal
        isVisible={showWelcomeModal}
        confirmText={'Weiter'}
        contentText={
          'In dieser Demo-Version können Sie ausprobieren, wie das Üben mit der App funktioniert. Wählen Sie zuerst ein Übungsset aus und tippen Sie dann auf „weiter“. Danach wählen Sie die Aufgaben aus und tippen auf „Start“.'
        }
        confirmAction={() => setShowWelcomeModal((prev) => !prev)}
        headerText={'Willkommen in der neolexon Aphasie-App für Patient:innen!'}
        noBackground
        hasBorder
        minimized
      />
    </SafeAreaView>
  );
}
